import moment from 'moment';
import { Reducer } from 'redux';
import { formatDate } from '../../../utils/formatDate';
import { getVolumeValueToShow } from '../../../utils/measureUnit';
import { EMeasureUnit } from '../../commomTypes';
import { EOrderTab } from '../long-steel-orders/types';
import { ECustomerTypeView } from '../user/types';
import {
  Billing,
  ELongSteelOrdersStatus,
  FutureDelivery,
  IBilledOrderTracking,
  ICgCertificateResponse,
  IDownloadNFEResponse,
  INewLongSteelOrdersResponse,
  INewLongSteelOrdersState,
  IQCFileResponse,
  ITabOrderDataState,
  ITrackingFlatSteel,
  ITrackingLongSteel,
  IUpdateItems,
  SalesOrderItem,
  SalesOrderItemTable,
  SalesOrderItemTableData,
  Types
} from './types';

export function toNumberOrZero(value: string | number) {
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
}

export const LONG_STEEL_ORDERS_TAB_INITIAL_STATE: ITabOrderDataState = {
  items: [],
  ordersFiltered: [],
  initialInputValues: {
    states: [],
    emitterIds: [],
    receiverId: '',
    startDate: moment().subtract(1, 'months').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    orderStatus: ELongSteelOrdersStatus.ALL
  },
  lastLoadTime: undefined,
  initialized: false,
  orderDetail: []
};

export const LONG_STEEL_ORDERS_INITIAL_STATE: INewLongSteelOrdersState = {
  loading: false,
  tableType: EOrderTab.OPEN,
  open: { ...LONG_STEEL_ORDERS_TAB_INITIAL_STATE },
  billed: { ...LONG_STEEL_ORDERS_TAB_INITIAL_STATE },
  updateItems: {
    itemsSelected: [],
    itemsToUpdate: [],
    itemsAllowed: [],
    downloadsItemsSelected: [],
    loading: {
      cancelOrders: false,
      downloadOrders: false,
      exportOrders: false,
      repeatOrders: false,
      validationOrders: false
    }
  },
  error: false,
  divisions: [],
  receivers: [],
  emitters: [],
  bpError: false
};

type TCustomAction = {
  type: Types;
  payload: {
    ordersResponse: INewLongSteelOrdersResponse & { orderType?: EOrderTab };

    downloadsItemsSelected: SalesOrderItemTableData[];

    updateItems: IUpdateItems;

    ordersFiltered: SalesOrderItemTableData[];

    billings: Billing[];

    bpError: boolean;

    openOrderItem: SalesOrderItemTable;
    openOrderItemId: string;

    trackingFlatSteel: ITrackingFlatSteel;
    trackingLongSteel: ITrackingLongSteel;

    billedOrderItem: SalesOrderItem;
    orderDetail: SalesOrderItem[];
    order: SalesOrderItem;
    orders: SalesOrderItem[];
    billedOrderTracking: IBilledOrderTracking;

    customerViewType?: ECustomerTypeView;

    qcFileResponse: IQCFileResponse;
    downloadNFEResponse: IDownloadNFEResponse;

    states: string[];
    customerIds: string[];
    startDate?: string;
    endDate?: string;

    loading?: boolean;

    tableType?: EOrderTab;

    cgCqResponse: ICgCertificateResponse;
  };
};

const reducer: Reducer<INewLongSteelOrdersState, TCustomAction> = (
  state = LONG_STEEL_ORDERS_INITIAL_STATE,
  action
) => {
  switch (action.type) {
    case Types.LOAD_NEW_LONG_STEEL_ORDERS:
    case Types.LOAD_NEW_LONG_STEEL_ORDERS_FROM_LOCAL_STORAGE: {
      const targetTab =
        action.payload.tableType || state.tableType || EOrderTab.OPEN;
      return {
        ...state,
        [targetTab]: {
          ...state[targetTab],
          initialInputValues: {
            ...state[targetTab].initialInputValues,
            states: action.payload.states || [],
            emitterIds: action.payload.customerIds || [],
            startDate: action.payload.startDate,
            endDate: action.payload.endDate
          },
          items: [],
          loading: true,
          initialized: true,
          lastLoadTime: undefined
        },
        receivers: [],
        divisions: [],
        loading: true,
        error: false
      };
    }
    case Types.LOAD_NEW_LONG_STEEL_ORDERS_SUCCESS: {
      const targetTab =
        action.payload.ordersResponse.orderType ||
        state.tableType ||
        EOrderTab.OPEN;
      return {
        ...state,
        [targetTab]: {
          ...state[targetTab],
          items: action.payload.ordersResponse.items,
          lastLoadTime: action.payload.ordersResponse.lastLoadTime,
          ordersFiltered:
            action.payload.ordersResponse.ordersFiltered ||
            action.payload.ordersResponse.items
        },
        loading: false,
        error: false,
        divisions: action.payload.ordersResponse.divisions || state.divisions,
        receivers: action.payload.ordersResponse.receivers || state.receivers,
        emitters: action.payload.ordersResponse.emitters || state.emitters,
        updateItems:
          action.payload.ordersResponse.updateItems || state.updateItems
      };
    }
    case Types.LOAD_NEW_LONG_STEEL_ORDERS_FAILURE: {
      const targetTab =
        action.payload.tableType || state.tableType || EOrderTab.OPEN;
      return {
        ...state,
        [targetTab]: {
          ...state[targetTab],
          items: []
        },
        loading: false,
        error: true,
        bpError: action.payload.bpError
      };
    }

    case Types.LOAD_NEW_LONG_STEEL_ORDER_DETAIL: {
      const targetTab =
        action.payload.tableType || state.tableType || EOrderTab.OPEN;
      return {
        ...state,
        [targetTab]: {
          ...state[targetTab],
          orderDetail: action.payload.orderDetail || []
        },
        loading: true
      };
    }
    case Types.LOAD_NEW_LONG_STEEL_ORDER_DETAIL_SUCCESS: {
      const targetTab =
        action.payload.tableType || state.tableType || EOrderTab.OPEN;
      return {
        ...state,
        [targetTab]: {
          ...state[targetTab],
          orderDetail: action.payload.orderDetail || []
        },
        loading: false,
        error: false
      };
    }
    case Types.LOAD_NEW_LONG_STEEL_ORDER_DETAIL_FAILURE:
      return {
        ...state,
        loading: false,
        error: true
      };

    // OPEN ORDER TRACKING FLAT STEEL
    case Types.LOAD_NEW_TRACKING_FLAT_STEEL:
      return {
        ...state,
        open: {
          ...state.open,
          items: setTrackingItemFlatSteelStateInOpenOrder(
            state.open.items,
            action.payload.openOrderItem,
            true,
            false
          )
        }
      };
    case Types.LOAD_NEW_TRACKING_FLAT_STEEL_SUCCESS:
      return {
        ...state,
        open: {
          ...state.open,
          items: setTrackingItemFlatSteelStateInOpenOrder(
            state.open.items,
            action.payload.openOrderItem,
            false,
            false,
            action.payload.trackingFlatSteel
          )
        }
      };
    case Types.LOAD_NEW_TRACKING_FLAT_STEEL_FAILURE:
      return {
        ...state,
        open: {
          ...state.open,
          items: setTrackingItemFlatSteelStateInOpenOrder(
            state.open.items,
            action.payload.openOrderItem,
            false,
            true
          )
        }
      };

    // OPEN ORDER TRACKING LONG STEEL
    case Types.LOAD_NEW_TRACKING_LONG_STEEL:
      return {
        ...state,
        open: {
          ...state.open,
          items: setTrackingItemLongSteelStateInOpenOrder(
            state.open.items,
            action.payload.openOrderItemId,
            true,
            false
          )
        }
      };
    case Types.LOAD_NEW_TRACKING_LONG_STEEL_SUCCESS:
      return {
        ...state,
        open: {
          ...state.open,
          items: setTrackingItemLongSteelStateInOpenOrder(
            state.open.items,
            action.payload.openOrderItemId,
            false,
            false,
            action.payload.trackingLongSteel
          )
        }
      };
    case Types.LOAD_NEW_TRACKING_LONG_STEEL_FAILURE:
      return {
        ...state,
        open: {
          ...state.open,
          items: setTrackingItemLongSteelStateInOpenOrder(
            state.open.items,
            action.payload.openOrderItemId,
            false,
            true
          )
        }
      };

    // BILLED ORDER TRACKING
    case Types.LOAD_NEW_BILLED_ORDER_TRACKING:
      return {
        ...state,
        billed: {
          ...state.billed,
          items: setTrackingItemSteelStateInBilledOrder(
            state.billed.items,
            action.payload.billedOrderItem,
            true,
            false
          )
        }
      };
    case Types.LOAD_NEW_BILLED_ORDER_TRACKING_SUCCESS:
      return {
        ...state,
        billed: {
          ...state.billed,
          items: setTrackingItemSteelStateInBilledOrder(
            state.billed.items,
            action.payload.billedOrderItem,
            false,
            false,
            action.payload.billedOrderTracking
          )
        }
      };
    case Types.LOAD_NEW_BILLED_ORDER_TRACKING_FAILURE:
      return {
        ...state,
        billed: {
          ...state.billed,
          items: setTrackingItemSteelStateInBilledOrder(
            state.billed.items,
            action.payload.billedOrderItem,
            false,
            true
          )
        }
      };

    // CERTIFICADO DE QUALIDADE
    case Types.LOAD_NEW_QC_FILE:
    case Types.CG_NEW_QC_FILE_REQUEST:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            downloadOrders: true
          }
        }
      };
    case Types.LOAD_NEW_QC_FILE_SUCCESS:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            downloadOrders: false
          }
        }
      };
    case Types.LOAD_NEW_QC_FILE_FAILURE:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            downloadOrders: false
          }
        }
      };

    case Types.CG_NEW_QC_FILE_REQUEST_SUCCESS:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            downloadOrders: false
          }
        },
        billed: {
          ...state.billed,
          items: setStatusOnBilledOrderItem(
            state.billed.items,
            action.payload.order,
            false,
            false,
            action.payload.cgCqResponse
          )
        }
      };
    case Types.CG_NEW_QC_FILE_REQUEST_FAILURE:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            downloadOrders: false
          }
        },
        billed: {
          ...state.billed,
          items: setStatusOnBilledOrderItem(
            state.billed.items,
            action.payload.order,
            false,
            true
          )
        }
      };
    case Types.CG_NEW_QC_FILE_DOWNLOAD:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            downloadOrders: false
          }
        },
        billed: {
          ...state.billed,
          items: setStatusOnBilledOrderItem(
            state.billed.items,
            action.payload.order,
            false,
            false,
            undefined,
            true
          )
        }
      };
    case Types.DOWNLOAD_NEW_QC_FILE:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            downloadOrders: false
          }
        },
        billed: {
          ...state.billed,
          items: setQCStateInBilledOrder(
            state.billed.items,
            action.payload.order,
            false,
            false,
            action.payload.order.qcState?.qcFileResponse as IQCFileResponse,
            true
          )
        }
      };

    // NF
    case Types.LOAD_NEW_NF_FILE:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            downloadOrders: true
          }
        }
      };
    case Types.LOAD_NEW_NF_FILE_SUCCESS:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            downloadOrders: false
          }
        }
      };
    case Types.LOAD_NEW_NF_FILE_FAILURE:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            downloadOrders: false
          }
        }
      };
    case Types.DOWNLOAD_NEW_NF_FILE:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            downloadOrders: false
          }
        },
        billed: {
          ...state.billed,
          items: setNFStateInBilledOrder(
            state.billed.items,
            action.payload.order,
            false,
            false,
            action.payload.order.nfState?.downloadNFEResponse,
            true
          )
        }
      };

    // NOTA DE REMESSA
    case Types.LOAD_NEW_SHIPPING_NOTE_FILE:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            downloadOrders: true
          }
        }
      };
    case Types.LOAD_NEW_SHIPPING_NOTE_FILE_SUCCESS:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            downloadOrders: false
          }
        }
      };
    case Types.LOAD_NEW_SHIPPING_NOTE_FILE_FAILURE:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            downloadOrders: false
          }
        }
      };
    case Types.DOWNLOAD_NEW_SHIPPING_NOTE_FILE:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            downloadOrders: false
          }
        },
        billed: {
          ...state.billed,
          items: setShippingNoteStateInBilledOrder(
            state.billed.items,
            action.payload.order,
            false,
            false,
            action.payload.order.shippingNoteState
              ? action.payload.order.shippingNoteState.downloadNFEResponse
              : undefined,
            true
          )
        }
      };

    case Types.CLEAR_NEW_ORDERS_STATE: {
      const isNotValid =
        action.payload.customerViewType === undefined ||
        action.payload.customerViewType === ECustomerTypeView.RECEIVER ||
        action.payload.customerViewType ===
          ECustomerTypeView.FABRICATION_RECEIVER;

      const isEmitter =
        action.payload.customerViewType === undefined ||
        action.payload.customerViewType === ECustomerTypeView.EMITTER;

      return {
        ...state,
        open: {
          ...LONG_STEEL_ORDERS_TAB_INITIAL_STATE,
          initialInputValues: {
            ...LONG_STEEL_ORDERS_TAB_INITIAL_STATE.initialInputValues,
            states: isNotValid ? [] : state.open.initialInputValues.states,
            emitterIds: isNotValid
              ? []
              : state.open.initialInputValues.emitterIds,
            receiverId: isEmitter
              ? ''
              : state.open.initialInputValues.receiverId
          }
        },
        billed: {
          ...LONG_STEEL_ORDERS_TAB_INITIAL_STATE,
          initialInputValues: {
            ...LONG_STEEL_ORDERS_TAB_INITIAL_STATE.initialInputValues,
            states: isNotValid ? [] : state.billed.initialInputValues.states,
            emitterIds: isNotValid
              ? []
              : state.billed.initialInputValues.emitterIds,
            receiverId: isEmitter
              ? ''
              : state.billed.initialInputValues.receiverId
          }
        }
      };
    }

    case Types.DOWNLOAD_WALLET:
      return {
        ...state,
        loadingDownloadOrders: !!action.payload.loading
      };
    case Types.UPDATE_FILTERED_ORDERS: {
      const targetTab = state.tableType || EOrderTab.OPEN;
      return {
        ...state,
        [targetTab]: {
          ...state[targetTab],
          ordersFiltered: action.payload.ordersFiltered
        }
      };
    }

    case Types.LOAD_UPDATE_ITEMS:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          itemsSelected: action.payload.updateItems.itemsSelected
            ? action.payload.updateItems.itemsSelected
            : state.updateItems.itemsSelected,
          loading: {
            ...state.updateItems.loading,
            cancelOrders: true
          }
        }
      };
    case Types.LOAD_UPDATE_ITEMS_SUCCESS:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          updateItems: {
            ...state.updateItems,
            itemsSelected: action.payload.updateItems.itemsSelected
          },
          loading: {
            ...state.updateItems.loading,
            cancelOrders: false
          }
        }
      };
    case Types.LOAD_UPDATE_ITEMS_FAILURE:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            cancelOrders: false
          }
        }
      };

    case Types.VALIDATION_UPDATE_ITEMS:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            validationOrders: true
          },
          typeAction: action.payload.updateItems.typeAction,
          itemsSelected: action.payload.updateItems.itemsSelected
        }
      };
    case Types.VALIDATION_UPDATE_ITEMS_SUCCESS:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          typeAction: undefined,
          itemsAllowed: action.payload.updateItems.itemsAllowed,
          itemsSelected: action.payload.updateItems.itemsSelected,
          loading: {
            ...state.updateItems.loading,
            validationOrders: false
          }
        }
      };
    case Types.VALIDATION_UPDATE_ITEMS_FAILURE:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            validationOrders: false
          },
          typeAction: undefined,
          itemsSelected: [],
          itemsAllowed: []
        }
      };

    case Types.DOWNLOADS_ITEMS:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          downloadsItemsSelected: action.payload.downloadsItemsSelected
        }
      };
    case Types.REPEAT_ORDERS:
      return {
        ...state,
        updateItems: {
          ...state.updateItems,
          loading: {
            ...state.updateItems.loading,
            repeatOrders: action.payload.updateItems.loading.repeatOrders
          }
        }
      };

    case Types.UPADTE_TABLE_TYPE:
      return {
        ...state,
        tableType: action.payload.tableType || EOrderTab.OPEN
      };

    case Types.LOAD_XML:
      return {
        ...state,
        loading: true
      };

    case Types.LOAD_XML_SUCCESS:
    case Types.LOAD_XML_FAILURE:
      return {
        ...state,
        loading: false
      };

    default:
      return state;
  }
};

const getItemFromLongSteelData = (
  items: SalesOrderItem[],
  selectedItemId: string
) => {
  return items.findIndex((item) => item.id === selectedItemId);
};

const setTrackingItemFlatSteelStateInOpenOrder = (
  items: SalesOrderItem[],
  selectedItem: SalesOrderItemTable,
  loading: boolean,
  error: boolean,
  trackingFlatSteel?: ITrackingFlatSteel
): SalesOrderItem[] => {
  const selectedItemIdx = getItemFromLongSteelData(items, selectedItem.id);
  if (selectedItemIdx !== -1) {
    (items[selectedItemIdx] as SalesOrderItemTable).trackingFlatSteel = {
      loading,
      error,
      flatsteel: trackingFlatSteel
    };
  }

  return items;
};

const setTrackingItemLongSteelStateInOpenOrder = (
  items: SalesOrderItem[],
  openOrderItemId: string,
  loading: boolean,
  error: boolean,
  trackingLongSteel?: ITrackingLongSteel
): SalesOrderItem[] => {
  const selectedItemIdx = getItemFromLongSteelData(items, openOrderItemId);
  if (selectedItemIdx !== -1) {
    (items[selectedItemIdx] as SalesOrderItemTable).trackingLongSteel = {
      loading,
      error,
      longSteel: trackingLongSteel
    };
  }

  return items;
};

const setTrackingItemSteelStateInBilledOrder = (
  items: SalesOrderItem[],
  selectedItem: SalesOrderItem,
  loading: boolean,
  error: boolean,
  billedOrderTracking?: IBilledOrderTracking
): SalesOrderItem[] => {
  const selectedItemIdx = getItemFromLongSteelData(items, selectedItem.id);
  if (selectedItemIdx !== -1) {
    (items[selectedItemIdx] as SalesOrderItem).trackingState = {
      loading,
      error,
      tracking: billedOrderTracking
    };
  }

  return items;
};

const setQCStateInBilledOrder = (
  items: SalesOrderItem[],
  selectedItem: SalesOrderItem,
  loading: boolean,
  error: boolean,
  qcFileResponse?: IQCFileResponse,
  alreadyDownloaded?: boolean
): SalesOrderItem[] => {
  const selectedItemIdx = getItemFromLongSteelData(items, selectedItem.id);
  if (selectedItemIdx !== -1) {
    const foundItem = items[selectedItemIdx] as SalesOrderItem;
    foundItem.qcState = {
      ...foundItem.qcState,
      loading,
      error,
      qcFileResponse,
      alreadyDownloaded
    };
  }

  return items;
};

const setNFStateInBilledOrder = (
  items: SalesOrderItem[],
  selectedItem: SalesOrderItem,
  loading: boolean,
  error: boolean,
  downloadNFEResponse?: IDownloadNFEResponse,
  alreadyDownloaded?: boolean
): SalesOrderItem[] => {
  const selectedItemIdx = getItemFromLongSteelData(items, selectedItem.id);
  if (selectedItemIdx !== -1) {
    (items[selectedItemIdx] as SalesOrderItem).nfState = {
      ...(items[selectedItemIdx] as SalesOrderItem).nfState,
      loading,
      error,
      downloadNFEResponse,
      alreadyDownloaded
    };
  }

  return items;
};

const setShippingNoteStateInBilledOrder = (
  items: SalesOrderItem[],
  selectedItem: SalesOrderItem,
  loading: boolean,
  error: boolean,
  downloadNFEResponse?: IDownloadNFEResponse,
  alreadyDownloaded?: boolean
): SalesOrderItem[] => {
  const selectedItemIdx = getItemFromLongSteelData(items, selectedItem.id);
  if (selectedItemIdx !== -1) {
    (items[selectedItemIdx] as SalesOrderItem).shippingNoteState = {
      ...(items[selectedItemIdx] as SalesOrderItem).shippingNoteState,
      loading,
      error,
      downloadNFEResponse,
      alreadyDownloaded
    };
  }

  return items;
};

const setStatusOnBilledOrderItem = (
  items: SalesOrderItem[],
  // futuramente será um array?
  selectedItem: SalesOrderItem,
  loading: boolean,
  error: boolean,
  cgQcResponse?: ICgCertificateResponse,
  alreadyDownloaded = false
): SalesOrderItem[] => {
  const idx = getItemFromLongSteelData(items, selectedItem.id);
  if (idx !== -1) {
    const item = items[idx] as SalesOrderItem;
    if (cgQcResponse) {
      // Também mudará quando download em batch existir
      const response = cgQcResponse.content[0];
      item.qcState = {
        ...item.qcState,
        cgQcCertificateResponse: response
      };
    }
    item.qcState = {
      ...item.qcState,
      loading,
      error,
      alreadyDownloaded
    };
  }
  return items;
};

export const getNextFutureDeliveryQuantity = (
  futureDeliveries: FutureDelivery[] | undefined
): number | undefined => {
  if (!futureDeliveries) {
    return undefined;
  }
  let quantity = 0;
  futureDeliveries.forEach((delivery) => {
    const deliveryQuantity =
      delivery.estimatedDeliveryMeasureUnit === EMeasureUnit.KG ||
      delivery.estimatedDeliveryMeasureUnit === EMeasureUnit.T
        ? Number(delivery.estimatedDeliveryQuantity)
            .toFixed(2)
            .toString()
            .replace('.', ',')
        : Number(delivery.estimatedDeliveryQuantity).toFixed(0);

    quantity += Number(deliveryQuantity);
  });
  if (!quantity) {
    return undefined;
  }
  return quantity;
};

export const getNextFutureDeliveryUnit = (
  futureDeliveries: FutureDelivery[] | undefined
): string | undefined => {
  if (!futureDeliveries) {
    return undefined;
  }
  let unit;
  futureDeliveries.forEach((delivery) => {
    unit = delivery.estimatedDeliveryMeasureUnit;
  });
  return unit;
};

export const getNextFutureDeliveryMinDate = (
  futureDeliveries: FutureDelivery[] | undefined
): string | undefined => {
  if (!futureDeliveries) {
    return undefined;
  }
  let minDate: string | undefined;
  futureDeliveries.forEach((delivery) => {
    if (!minDate || delivery.estimatedDeliveryDate < minDate) {
      minDate = delivery.estimatedDeliveryDate;
    }
  });
  return minDate;
};

export const getNextFutureDeliveryMaxDate = (
  futureDeliveries: FutureDelivery[] | undefined
): string | undefined => {
  if (!futureDeliveries) {
    return undefined;
  }
  let maxDate: string | undefined;
  futureDeliveries.forEach((delivery) => {
    if (!maxDate || delivery.estimatedDeliveryDate > maxDate) {
      maxDate = delivery.estimatedDeliveryDate;
    }
  });
  return maxDate;
};

export const getDeliveryStatusText = ({
  estimatedDeliveryDate,
  hasAnyBiiledQuantity
}: {
  estimatedDeliveryDate?: string;
  hasAnyBiiledQuantity: boolean;
}): string => {
  if (!estimatedDeliveryDate) {
    if (hasAnyBiiledQuantity) {
      return 'Faturado';
    }
    return 'Aguardando confirmação';
  }

  const today = moment();
  let deliveryDate;

  if (estimatedDeliveryDate.length > 10) {
    // Caso retorne no formato "DD/MM/YYYY à DD/MM/YYYY", pega a última data do range
    const splitedDate = estimatedDeliveryDate.split(' ');
    deliveryDate = moment(splitedDate[splitedDate.length - 1], 'DD/MM/YYYY');
  } else {
    deliveryDate = moment(estimatedDeliveryDate, 'DD/MM/YYYY');
  }

  // Verifica se a data de hoje é igual ou anterior à data de entrega (comparando apenas o dia)
  if (today.isSameOrBefore(deliveryDate, 'day')) {
    return 'Dentro do prazo';
  } else {
    return 'Com atraso';
  }
};

export const convertFilteredOrdersToIOpenOrdersTableDataSource = (
  dataInvoices: SalesOrderItem[]
): SalesOrderItemTable[] => {
  return dataInvoices.map((item) => {
    const dynamicDeliveryProperties =
      item.futureDeliveries?.reduce((acc, delivery, index) => {
        const deliveryQuantity =
          delivery.estimatedDeliveryMeasureUnit === EMeasureUnit.KG ||
          delivery.estimatedDeliveryMeasureUnit === EMeasureUnit.T
            ? Number(delivery.estimatedDeliveryQuantity)
                .toFixed(2)
                .toString()
                .replace('.', ',')
            : Number(delivery.estimatedDeliveryQuantity).toFixed(0);

        acc[`futureDelivery_${index}_date`] = delivery.estimatedDeliveryDate;
        acc[`futureDelivery_${index}_quantity`] = deliveryQuantity;
        acc[`futureDelivery_${index}_unit`] =
          delivery.estimatedDeliveryMeasureUnit;
        return acc;
      }, {} as { [key: string]: string }) ?? {};

    return {
      ...item,
      key: item.id,
      id: item.id,
      emitter: item.soldTo.name,
      emitterCode: item.soldTo.code,
      materialDescription:
        item?.billingHistory?.materialDescription ?? item.materialDescription,
      salesOrder:
        item.salesOrder?.toString() ||
        item?.billingHistory?.salesOrder?.toString(),
      salesOrderItem:
        item.salesOrderItem?.toString() ||
        item?.billingHistory?.salesOrderItem?.toString(),
      customerOrder:
        item.customerOrder || item?.billingHistory?.customerOrder?.toString(),
      quantities: item.quantities,
      productState: item.quantities,
      numberOrder:
        item.salesOrderItem?.toString() ||
        item?.billingHistory?.salesOrderItem?.toString(),
      requestedDate: item.requestedDate,
      requestedDateTable: formatDate(item.requestedDate, 'DD/MM/YYYY'),
      futureDeliveries: item.futureDeliveries,
      nextFutureDeliveryQuantity: getNextFutureDeliveryQuantity(
        item.futureDeliveries
      ),
      nextFutureDeliveryUnit: getNextFutureDeliveryUnit(item.futureDeliveries),
      nextFutureDeliveryMinDate: getNextFutureDeliveryMinDate(
        item.futureDeliveries
      ),
      nextFutureDeliveryMaxDate: getNextFutureDeliveryMaxDate(
        item.futureDeliveries
      ),
      futureDeliveriesTable: getDeliveryStatusText({
        estimatedDeliveryDate:
          item?.futureDeliveries && item?.futureDeliveries?.length > 0
            ? item?.futureDeliveries[item?.futureDeliveries?.length - 1]
                ?.estimatedDeliveryDate
            : undefined,
        hasAnyBiiledQuantity:
          toNumberOrZero(
            item?.billing?.reduce(
              (itmAcc, itmItem) =>
                itmAcc + (Number(itmItem.billedConversionQuantity) || 0),
              0
            )
          ) > 0 || Number(item?.quantities?.billedQuantity || 0) > 0
      }),
      creationDate: item.itemCreationDate,
      creationDateTable: item?.billingHistory?.itemCreationDate
        ? formatDate(item.billingHistory.itemCreationDate, 'DD/MM/YYYY')
        : formatDate(item.itemCreationDate, 'DD/MM/YYYY'),
      receiver: item.shipTo.name,
      receiverCode: item.shipTo.code,
      orderType: item.orderType,
      divisionDescription: item.divisionDescription,
      totalQuantityConfirmed: getVolumeValueToShow(
        item?.quantities.requestedQuantity,
        item?.quantities.requestedMeasureUnit
      ),
      totalQuantityRequested: getVolumeValueToShow(
        item?.quantities.requestedQuantity,
        item?.quantities.requestedMeasureUnit
      ),
      totalQuantityBilled: getVolumeValueToShow(
        toNumberOrZero(
          item?.billing?.reduce(
            (itmAcc, itmItem) => itmAcc + (Number(itmItem.billedQuantity) || 0),
            0
          ) || item?.quantities.billedQuantity
        ),
        item?.quantities.billedMeasureUnit ??
          item?.quantities.requestedMeasureUnit
      ),
      totalQuantityPending: getVolumeValueToShow(
        item?.quantities.pendingQuantity,
        item?.quantities.pendingMeasureUnit ??
          item?.quantities.requestedMeasureUnit
      ),
      totalQuantityDelivery: getVolumeValueToShow(
        item?.quantities.deliveryQuantity,
        item?.quantities.deliveryMeasureUnit ??
          item?.quantities.requestedMeasureUnit
      ),
      totalQuantityShipment: getVolumeValueToShow(
        item?.quantities.shipmentQuantity,
        item?.quantities.shipmentMeasureUnit ??
          item?.quantities.requestedMeasureUnit
      ),
      flatSteelProduction: item?.flatSteelProduction,
      productionStatus: `${item?.flatSteelProduction?.rollingMill ?? ''}${
        item?.flatSteelProduction?.meltShop ?? ''
      }${item?.flatSteelProduction?.rollingFinish ?? ''}${
        item?.flatSteelProduction?.finish ?? ''
      }${item?.flatSteelProduction?.stockMill ?? ''}${
        item?.flatSteelProduction?.transit ?? ''
      }${item?.flatSteelProduction?.stockWarehouse ?? ''}`,
      meltShop: getVolumeValueToShow(
        item?.flatSteelProduction?.meltShop,
        EMeasureUnit.T
      ),
      rollingMill: getVolumeValueToShow(
        item?.flatSteelProduction?.rollingMill,
        EMeasureUnit.T
      ),
      rollingFinish: getVolumeValueToShow(
        item?.flatSteelProduction?.rollingFinish,
        EMeasureUnit.T
      ),
      finish: getVolumeValueToShow(
        item?.flatSteelProduction?.finish,
        EMeasureUnit.T
      ),
      stockMill: getVolumeValueToShow(
        item?.flatSteelProduction?.stockMill,
        EMeasureUnit.T
      ),
      transit: getVolumeValueToShow(
        item?.flatSteelProduction?.transit,
        EMeasureUnit.T
      ),
      stockWarehouse: getVolumeValueToShow(
        item?.flatSteelProduction?.stockWarehouse,
        EMeasureUnit.T
      ),
      item: item,
      ...dynamicDeliveryProperties
    };
  });
};

export function formatCurrencyValue(textNumber: string): string {
  if (!textNumber || Number.isNaN(Number(textNumber))) {
    return textNumber;
  }

  return Number(textNumber).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    style: 'currency',
    currency: 'BRL'
  });
}

export function isUnavailable(value: string): boolean {
  return (
    !value ||
    value === 'Indisponível' ||
    Number.isNaN(value) ||
    Number(value) === 0
  );
}

export function valueValidation(value?: string): string {
  if (!value || Number.isNaN(value) || Number(value) === 0) {
    return 'Indisponível';
  }

  return value;
}

export default reducer;
